/**
 * 辅助工具函数
 * 功能：提供常用的工具函数
 * 作者：系统开发团队
 * 创建时间：2023-12-01
 */

import moment from 'moment';

/**
 * 格式化时间
 * @param {string|Date} date - 时间
 * @param {string} format - 格式
 * @returns {string} 格式化后的时间
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
    if (!date) return '';
    return moment(date).format(format);
}

/**
 * 格式化相对时间
 * @param {string|Date} date - 时间
 * @returns {string} 相对时间
 */
export function formatRelativeTime(date) {
    if (!date) return '';
    return moment(date).fromNow();
}

/**
 * 格式化金额
 * @param {number} amount - 金额
 * @param {string} currency - 货币符号
 * @returns {string} 格式化后的金额
 */
export function formatMoney(amount, currency = '¥') {
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    const num = Number(amount);
    if (isNaN(num)) return '';
    return `${currency}${num.toFixed(2)}`;
}

/**
 * 格式化价格区间
 * @param {number} minPrice - 最低价格
 * @param {number} maxPrice - 最高价格
 * @param {string} currency - 货币符号
 * @returns {string} 格式化后的价格区间
 */
export function formatPriceRange(specs, currency = '¥') {
    if (!Array.isArray(specs) || specs.length === 0) return '';
    
    const prices = specs.map(spec => Number(spec.price)).filter(price => !isNaN(price));
    if (prices.length === 0) return '';
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
        return formatMoney(minPrice, currency);
    }
    return `${formatMoney(minPrice, currency)}-${formatMoney(maxPrice, currency)}`;
}

/**
 * 生成随机字符串
 * @param {number} length - 长度
 * @returns {string} 随机字符串
 */
export function generateRandomString(length = 8) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

/**
 * 生成随机用户名
 * @returns {string} 随机用户名
 */
export function generateUsername() {
    return 'user' + Date.now() + Math.random().toString(36).substr(2, 4);
}

/**
 * 生成随机昵称
 * @returns {string} 随机昵称
 */
export function generateNickname() {
    const adjectives = ['快乐的', '聪明的', '可爱的', '勇敢的', '善良的', '活泼的', '温柔的', '帅气的'];
    const nouns = ['小明', '小红', '小刚', '小丽', '小华', '小强', '小美', '小亮'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    return adj + noun;
}

/**
 * 生成订单号
 * @returns {string} 订单号
 */
export function generateOrderNo() {
    return 'ORD' + Date.now() + Math.random().toString(36).substr(2, 5).toUpperCase();
}

/**
 * 生成设备ID
 * @returns {string} 设备ID
 */
export function generateDeviceId() {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
        deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 8);
        localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
}

/**
 * 深拷贝对象
 * @param {any} obj - 要拷贝的对象
 * @returns {any} 拷贝后的对象
 */
export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => deepClone(item));
    if (typeof obj === 'object') {
        const clonedObj = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                clonedObj[key] = deepClone(obj[key]);
            }
        }
        return clonedObj;
    }
}

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} delay - 延迟时间
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, delay = 300) {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

/**
 * 节流函数
 * @param {Function} func - 要节流的函数
 * @param {number} delay - 延迟时间
 * @returns {Function} 节流后的函数
 */
export function throttle(func, delay = 300) {
    let lastCall = 0;
    return function (...args) {
        const now = Date.now();
        if (now - lastCall >= delay) {
            lastCall = now;
            func.apply(this, args);
        }
    };
}

/**
 * 验证手机号
 * @param {string} phone - 手机号
 * @returns {boolean} 是否有效
 */
export function validatePhone(phone) {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
}

/**
 * 验证邮箱
 * @param {string} email - 邮箱
 * @returns {boolean} 是否有效
 */
export function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * 验证身份证号
 * @param {string} idCard - 身份证号
 * @returns {boolean} 是否有效
 */
export function validateIdCard(idCard) {
    const idCardRegex = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;
    return idCardRegex.test(idCard);
}

/**
 * 获取文件扩展名
 * @param {string} filename - 文件名
 * @returns {string} 扩展名
 */
export function getFileExtension(filename) {
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
}

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的大小
 */
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 获取图片URL
 * @param {string} filename - 文件名
 * @returns {string} 完整的图片URL
 */
export function getImageUrl(filename) {
    if (!filename) return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik00MCA0MEg2MFY2MEg0MFY0MFoiIGZpbGw9IiNDQ0MiLz4KPHBhdGggZD0iTTQ1IDQ1VjU1SDQ1VjQ1WiIgZmlsbD0iI0NDQyIvPgo8cGF0aCBkPSJNNTUgNDVWNTVINTVWNDVaIiBmaWxsPSIjQ0NDIi8+CjxwYXRoIGQ9Ik00MCA0MEg2MFY2MEg0MFY0MFoiIHN0cm9rZT0iI0NDQyIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSJub25lIi8+Cjx0ZXh0IHg9IjUwIiB5PSI3NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI0NDQyIgZm9udC1zaXplPSIxMiI+5Zu+54mHPC90ZXh0Pgo8L3N2Zz4K';
    if (filename.startsWith('http') || filename.startsWith('//') || filename.startsWith('data:image') || filename.startsWith('https')) return filename;
    return `/uploads/${filename}`;
}

/**
 * 获取用户头像URL
 * @param {string} avatar - 头像文件名
 * @returns {string} 完整的头像URL
 */
export function getAvatarUrl(avatar) {
    if (!avatar) return '/images/default-avatar.png';
    return getImageUrl(avatar);
}

/**
 * 计算购物车总价
 * @param {Array} cartItems - 购物车商品
 * @returns {number} 总价
 */
export function calculateCartTotal(cartItems) {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
    }, 0);
}

/**
 * 计算购物车商品数量
 * @param {Array} cartItems - 购物车商品
 * @returns {number} 商品数量
 */
export function calculateCartQuantity(cartItems) {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, item) => {
        return total + item.quantity;
    }, 0);
}

/**
 * 获取订单状态颜色
 * @param {string} status - 订单状态
 * @returns {string} 颜色类名
 */
export function getOrderStatusColor(status) {
    const colorMap = {
        pending: 'orange',
        confirmed: 'blue',
        preparing: 'purple',
        delivering: 'cyan',
        completed: 'green',
        cancelled: 'red'
    };
    return colorMap[status] || 'gray';
}

/**
 * 获取订单状态图标
 * @param {string} status - 订单状态
 * @returns {string} 图标类名
 */
export function getOrderStatusIcon(status) {
    const iconMap = {
        pending: 'clock',
        confirmed: 'check-circle',
        preparing: 'cog',
        delivering: 'truck',
        completed: 'check',
        cancelled: 'times'
    };
    return iconMap[status] || 'question';
}

/**
 * 检查是否为移动设备
 * @returns {boolean} 是否为移动设备
 */
export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 检查是否为iOS设备
 * @returns {boolean} 是否为iOS设备
 */
export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * 检查是否为Android设备
 * @returns {boolean} 是否为Android设备
 */
export function isAndroid() {
    return /Android/.test(navigator.userAgent);
}

/**
 * 获取浏览器信息
 * @returns {Object} 浏览器信息
 */
export function getBrowserInfo() {
    const ua = navigator.userAgent;
    const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    
    return {
        userAgent: ua,
        isMobile,
        isIOS,
        isAndroid,
        isDesktop: !isMobile
    };
}

/**
 * 生成二维码URL
 * @param {string} text - 二维码内容
 * @param {number} size - 二维码大小
 * @returns {string} 二维码URL
 */
export function generateQRCodeUrl(text, size = 200) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}`;
}

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @returns {Promise<boolean>} 是否复制成功
 */
export async function copyToClipboard(text) {
    try {
        if (navigator.clipboard) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // 兼容旧浏览器
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
        }
    } catch (err) {
        console.error('复制失败:', err);
        return false;
    }
}

/**
 * 下载文件
 * @param {string} url - 文件URL
 * @param {string} filename - 文件名
 */
export function downloadFile(url, filename) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * 获取URL参数
 * @param {string} name - 参数名
 * @returns {string|null} 参数值
 */
export function getUrlParam(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
}

/**
 * 设置URL参数
 * @param {string} name - 参数名
 * @param {string} value - 参数值
 */
export function setUrlParam(name, value) {
    const url = new URL(window.location);
    url.searchParams.set(name, value);
    window.history.replaceState({}, '', url);
}

/**
 * 移除URL参数
 * @param {string} name - 参数名
 */
export function removeUrlParam(name) {
    const url = new URL(window.location);
    url.searchParams.delete(name);
    window.history.replaceState({}, '', url);
}

/**
 * 格式化数字
 * @param {number} num - 数字
 * @param {number} decimals - 小数位数
 * @returns {string} 格式化后的数字
 */
export function formatNumber(num, decimals = 0) {
    if (num === null || num === undefined) return '';
    return Number(num).toFixed(decimals);
}

/**
 * 生成唯一ID
 * @returns {string} 唯一ID
 */
export function generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * 数组去重
 * @param {Array} arr - 数组
 * @param {string} key - 去重的键名（可选）
 * @returns {Array} 去重后的数组
 */
export function uniqueArray(arr, key = null) {
    if (!Array.isArray(arr)) return [];
    
    if (key) {
        const seen = new Set();
        return arr.filter(item => {
            const value = item[key];
            if (seen.has(value)) {
                return false;
            }
            seen.add(value);
            return true;
        });
    } else {
        return [...new Set(arr)];
    }
}

/**
 * 数组分组
 * @param {Array} arr - 数组
 * @param {string|Function} key - 分组的键名或函数
 * @returns {Object} 分组后的对象
 */
export function groupBy(arr, key) {
    if (!Array.isArray(arr)) return {};
    
    return arr.reduce((groups, item) => {
        const groupKey = typeof key === 'function' ? key(item) : item[key];
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(item);
        return groups;
    }, {});
}

/**
 * 数组排序
 * @param {Array} arr - 数组
 * @param {string} key - 排序的键名
 * @param {string} order - 排序方向 'asc' | 'desc'
 * @returns {Array} 排序后的数组
 */
export function sortArray(arr, key, order = 'asc') {
    if (!Array.isArray(arr)) return [];
    
    return arr.sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
    });
}

// 根据文件大小动态调整质量
function getDynamicQuality(fileSize, baseQuality = 0.8) {
  // 文件大小阈值（字节）
  const thresholds = [
    { size: 5 * 1024 * 1024, quality: 0.6 },  // 5MB+ 用60%质量
    { size: 3 * 1024 * 1024, quality: 0.7 },  // 3MB+ 用70%质量
    { size: 1 * 1024 * 1024, quality: 0.8 },  // 1MB+ 用80%质量
    { size: 0, quality: 0.9 }                 // 1MB以下用90%质量
  ];
  
  for (const threshold of thresholds) {
    if (fileSize >= threshold.size) {
      return Math.min(threshold.quality, baseQuality);
    }
  }
  
  return baseQuality;
}

// 根据图片尺寸动态调整质量
function getSizeBasedQuality(width, height, baseQuality = 0.8) {
  const pixels = width * height;
  
  if (pixels > 4000000) {      // 4MP+ 用较低质量
    return Math.min(0.7, baseQuality);
  } else if (pixels > 2000000) { // 2MP+ 用中等质量
    return Math.min(0.8, baseQuality);
  } else {                    // 2MP以下用高质量
    return Math.min(0.9, baseQuality);
  }
}

export function compressImage(file, quality = 0.8, maxWidth = 800, maxHeight = 600) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // 计算压缩后的尺寸
      let { width, height } = img;
      const originalPixels = width * height;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // 绘制压缩后的图片
      ctx.drawImage(img, 0, 0, width, height);
      
      // 动态调整质量
      const fileSize = file.size;
      const sizeBasedQuality = getSizeBasedQuality(width, height, quality);
      const dynamicQuality = getDynamicQuality(fileSize, sizeBasedQuality);
      
      console.log('图片压缩参数:', {
        原始大小: `${(fileSize / 1024 / 1024).toFixed(2)}MB`,
        原始尺寸: `${img.width}x${img.height}`,
        压缩尺寸: `${width}x${height}`,
        文件质量: `${(dynamicQuality * 100).toFixed(0)}%`,
        尺寸质量: `${(sizeBasedQuality * 100).toFixed(0)}%`,
        最终质量: `${(dynamicQuality * 100).toFixed(0)}%`
      });
      
      // 转换为 Blob
      canvas.toBlob((blob) => {
        // 创建新的 File 对象，保持原始文件名但改为 .jpg 扩展名
        const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
        const compressedFile = new File([blob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        
        console.log('压缩结果:', {
          原始大小: `${(fileSize / 1024).toFixed(2)}KB`,
          压缩大小: `${(blob.size / 1024).toFixed(2)}KB`,
          压缩率: `${((1 - blob.size / fileSize) * 100).toFixed(1)}%`
        });
        
        resolve(compressedFile);
      }, 'image/jpeg', dynamicQuality);
    };
    
    img.src = URL.createObjectURL(file);
  });
}

export default {
    formatDate,
    formatRelativeTime,
    formatMoney,
    formatPriceRange,
    generateRandomString,
    generateUsername,
    generateNickname,
    generateOrderNo,
    generateDeviceId,
    deepClone,
    debounce,
    throttle,
    validatePhone,
    validateEmail,
    validateIdCard,
    getFileExtension,
    formatFileSize,
    getImageUrl,
    getAvatarUrl,
    calculateCartTotal,
    calculateCartQuantity,
    getOrderStatusColor,
    getOrderStatusIcon,
    isMobile,
    isIOS,
    isAndroid,
    getBrowserInfo,
    generateQRCodeUrl,
    copyToClipboard,
    downloadFile,
    getUrlParam,
    setUrlParam,
    removeUrlParam,
    formatNumber,
    generateUniqueId,
    uniqueArray,
    groupBy,
    sortArray,
    compressImage
};


